import type { Competitor } from '../types';

// The baseline for the ecommerce comparisons: a warehouse Greyfield builds and
// hands over (Snowflake + dbt + a BI tool such as Sigma). Claims here must be
// backed by a case study, a published guide, or the service pages, and must
// not promise anything we have not delivered.

export const greyfield: Competitor = {
  id: 'greyfield',
  name: 'Custom warehouse (Greyfield)',
  shortName: 'a custom warehouse',
  group: 'ecomm',
  website: 'https://www.greyfielddata.com/ecommerce',
  summary:
    'A Snowflake warehouse with dbt models and a BI layer (usually Sigma), built around your sources and your definitions, that you own after handover.',
  bestFor: [
    'Brands selling across Shopify, Amazon, TikTok Shop, wholesale, or retail that need one set of numbers across channels.',
    'Finance and operations teams that need profit, refunds, fees, and inventory defined their own way.',
    'Portfolios with several brands or stores that need the same definitions applied to each one.',
    'Teams that want the data in their own warehouse so other tools and people can use it.',
  ],
  notFor: [
    'A single Shopify store that mainly needs marketing attribution and a daily dashboard.',
    'Teams that need numbers this week and have no one to own a warehouse after handover.',
    'Brands whose reporting questions are fully answered by a packaged tool already.',
  ],
  strengths: [
    'Any source with a connector or an export can be modeled, not only the platforms a vendor supports.',
    'Metric definitions live in dbt models you can read and change, so finance and marketing can agree on one version.',
    'The data stays in a warehouse account you control, which other BI tools, spreadsheets, or AI tools can query.',
  ],
  limitations: [
    'Slower to stand up than a packaged app: connectors are quick, but matching records, history, and business rules take weeks.',
    'You pay for the warehouse, connectors, and BI licences separately, and someone has to own the models after handover.',
    'No tracking pixel or built-in attribution model. Attribution data from a dedicated tool can be loaded into the warehouse instead.',
    'No industry benchmarks across other brands.',
  ],
  pricing: {
    model: 'Project-based build, optional ongoing support',
    publicStartingPrice: 'Scoped after a free consultation',
    freeTier: 'No',
    notes: 'Warehouse, connector, and BI licences are billed by those vendors directly to you.',
    sourceUrl: 'https://www.greyfielddata.com/ecommerce',
    checkedOn: '2026-11-03',
  },
  features: {
    shopify: { value: 'yes', note: 'Through a managed connector such as Fivetran or Airbyte.', sourceUrl: '/case-studies/dtc-brand-portfolio' },
    amazon: { value: 'yes', note: 'Orders, settlements, and fee events.', sourceUrl: '/blog/where-do-amazon-seller-fees-live-in-the-sp-api' },
    tiktok_shop: { value: 'yes', note: 'Modeled alongside Shopify and Amazon.', sourceUrl: '/blog/avoid-double-counting-shopify-amazon-tiktok-orders' },
    wholesale_retail_or_erp: { value: 'yes', note: 'Any source with a connector or export.', sourceUrl: '/case-studies/national-legal-organization' },
    ad_platforms: { value: 'yes', note: 'Ad spend modeled next to orders.', sourceUrl: '/case-studies/dtc-brand-portfolio' },
    first_party_pixel_attribution: { value: 'no', note: 'Attribution from a dedicated tool can be loaded in.', sourceUrl: '/case-studies/dtc-apparel-brand' },
    mmm_incrementality: { value: 'no', note: 'Not part of a standard build.' },
    profit_cogs: { value: 'yes', note: 'Fees, refunds, and cost timing defined in dbt.', sourceUrl: '/blog/why-amazon-profit-changes-after-sale' },
    ltv_cohorts: { value: 'yes', note: 'Contribution-margin LTV by acquisition channel.', sourceUrl: '/blog/contribution-margin-ltv-by-acquisition-channel' },
    subscription_analytics: { value: 'yes', note: 'Recharge renewals and skips modeled as events.', sourceUrl: '/blog/how-to-combine-shopify-klaviyo-recharge-data' },
    warehouse_access: { value: 'yes', note: 'Your own Snowflake account.', sourceUrl: '/services/snowflake-consulting' },
    custom_metrics: { value: 'yes', note: 'Definitions live in dbt models you own.', sourceUrl: '/blog/why-shopify-finance-marketing-report-different-revenue' },
    custom_sql: { value: 'yes', note: 'Full SQL in Snowflake and in Sigma.', sourceUrl: '/services/sigma-consulting' },
    ai_assistant: { value: 'partial', note: 'Depends on the BI tool; we have built text-to-SQL access for a client.', sourceUrl: '/case-studies/national-legal-organization' },
    mobile_app: { value: 'partial', note: 'Depends on the BI tool you choose.' },
  },
  recentChanges: [],
  sources: [
    { title: 'Ecommerce data warehouses', url: '/ecommerce' },
    { title: 'Multi-brand ecommerce portfolio case study', url: '/case-studies/dtc-brand-portfolio' },
    { title: 'DTC apparel brand case study', url: '/case-studies/dtc-apparel-brand' },
  ],
  lastReviewed: '2026-11-03',
};
